import { useCallback, useEffect, useState } from "react";
import { LoginRouterState } from "../../../api/auth";
import { useLocation } from "react-router-dom";

interface UseSnsLoginParams {
  handleLogin: (snsData?: LoginRouterState) => Promise<void>;
}

const useSnsLogin = ({ handleLogin }: UseSnsLoginParams) => {
  const location = useLocation();
  const locationState = location.state as LoginRouterState | null;
  const [isNaver, setIsNaver] = useState(false);
  const [naverEmail, setNaverEmail] = useState("");
  const [naverPopupType, setNaverPopupType] = useState("");

  /**
   * 네이버 로그인 (seller → web 콜백 공유)
   *
   * 네이버 개발자센터 Callback URL 등록 슬롯이 최대 5개라,
   * seller 전용 콜백을 추가하지 않고 web 콜백을 재사용
   *
   * 1. seller → 네이버 authorize 페이지 이동
   * 2. 네이버 → web `/snslogin/naverCallback`
   * 3. web에서 code → accessToken 교환 후, seller origin으로 리다이렉트
   *    -> `${seller}/login?loginType=SIMPLE_NAVER&accessToken=...`
   * 4. seller use-login의 콜백 useEffect가 query를 읽어 handleLogin 호출
   */
  const handleNaverLogin = useCallback(() => {
    const cId = process.env.REACT_APP_NAVER_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_HOST_URL}/snslogin/naverCallback`;
    const state = JSON.stringify({
      redirect: locationState?.redirect || "",
      returnOrigin: window.location.origin,
    });
    const url = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${cId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`;
    window.location.replace(url);
  }, [locationState?.redirect]);

  const decodeApplePayload = (token: string) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );
      return JSON.parse(payload);
    } catch (e) {
      alert("디코딩 실패");
      return null;
    }
  };

  // 애플 로그인
  const handleAppleLogin = useCallback(() => {
    window.AppleID.auth
      .signIn()
      .then((res: any) => {
        if (!res.authorization.id_token) {
          alert("SNS 로그인을 실패했습니다.");
          return;
        }
        // 애플 토큰 디코딩
        const decodeToken = decodeApplePayload(res.authorization.id_token);
        if (!decodeToken?.email) {
          alert("Apple 계정 이메일 정보를 확인할 수 없습니다.");
          return;
        }
        // 콜백 정보 설정
        const nextSnsData = {
          redirect: res.authorization?.state || "",
          loginType: "SIMPLE_APPLE",
          accessToken: res.authorization.id_token,
          textId: decodeToken.email.toLowerCase(),
        };
        // 로그인 처리
        handleLogin(nextSnsData);
      })
      .catch(() => {
        alert("SNS 로그인을 실패했습니다.");
      });
  }, [handleLogin]);

  // 애플 로그인 초기화
  useEffect(() => {
    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
        redirectURI: `${window.location.origin}/snslogin/appleCallback`,
        scope: "name email",
        state: locationState?.redirect || "",
        usePopup: true,
      });
    }
  }, [locationState?.redirect]);

  return {
    isNaver,
    setIsNaver,
    naverEmail,
    setNaverEmail,
    naverPopupType,
    setNaverPopupType,
    handleNaverLogin,
    handleAppleLogin,
  };
};

export default useSnsLogin;
